import React, { useState, useEffect } from 'react';
import { Star, Quote } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { testimonials } from '../data/mock';
import ReviewForm from './ReviewForm';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Testimonials = () => {
  const [approvedReviews, setApprovedReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch approved reviews from backend
    const fetchApprovedReviews = async () => {
      try {
        const response = await axios.get(`${API}/reviews/approved`);
        setApprovedReviews(response.data);
      } catch (error) {
        console.error('Error fetching reviews:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchApprovedReviews();
  }, []);

  // Combine mock testimonials with approved reviews from backend
  const allTestimonials = [
    ...testimonials,
    ...approvedReviews.map(review => ({
      id: `review-${review.id}`,
      name: review.display_name,
      location: review.location || 'Client',
      rating: review.rating,
      date: formatDate(review.created_at),
      text: review.comment,
      project: review.project_type
    }))
  ];

  function formatDate(dateString) {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 7) return `Il y a ${diffDays} jour${diffDays > 1 ? 's' : ''}`;
    if (diffDays < 30) return `Il y a ${Math.floor(diffDays / 7)} semaine${Math.floor(diffDays / 7) > 1 ? 's' : ''}`;
    return `Il y a ${Math.floor(diffDays / 30)} mois`;
  }

  // Calculate average rating
  const avgRating = allTestimonials.length > 0
    ? (allTestimonials.reduce((sum, t) => sum + t.rating, 0) / allTestimonials.length).toFixed(1)
    : '4.7';

  return (
    <section className="py-20 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-block bg-[#c9a961]/10 text-[#c9a961] px-4 py-2 rounded-full text-sm font-semibold mb-4">
            Témoignages Clients
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-[#0B1F3C] mb-6">
            Ils nous font confiance
          </h2>
          <p className="text-lg text-gray-600">
            Découvrez les retours de nos clients satisfaits sur leurs projets de rénovation.
          </p>
        </div>

        {/* Rating Summary */}
        <div className="max-w-md mx-auto mb-12 text-center">
          <div className="flex items-center justify-center gap-3 mb-3">
            <span className="text-5xl font-bold text-[#0B1F3C]">{avgRating}</span>
            <div>
              <div className="flex gap-1 mb-1">
                {[1, 2, 3, 4].map((star) => (
                  <Star key={star} className="fill-[#c9a961] text-[#c9a961]" size={24} />
                ))}
                <div className="relative">
                  <Star className="text-[#c9a961]" size={24} />
                  <Star 
                    className="fill-[#c9a961] text-[#c9a961] absolute top-0 left-0" 
                    size={24}
                    style={{ clipPath: 'inset(0 30% 0 0)' }}
                  />
                </div>
              </div>
              <p className="text-sm text-gray-600">Note moyenne</p>
            </div>
          </div>
        </div>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {allTestimonials.map((testimonial) => (
            <Card
              key={testimonial.id}
              className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 relative overflow-hidden group"
            >
              {/* Quote Icon */}
              <div className="absolute top-4 right-4 opacity-10 group-hover:opacity-20 transition-opacity">
                <Quote size={60} className="text-[#c9a961]" />
              </div>

              <CardContent className="p-6 relative">
                {/* Rating */}
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, index) => (
                    <Star key={index} className="fill-[#c9a961] text-[#c9a961]" size={18} />
                  ))}
                </div>

                {/* Review Text */}
                <p className="text-gray-700 leading-relaxed mb-6 italic">
                  "{testimonial.text}"
                </p>

                {/* Client Info */}
                <div className="border-t pt-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-bold text-[#0B1F3C]">{testimonial.name}</p>
                      <p className="text-sm text-gray-500">{testimonial.location}</p>
                      <p className="text-xs text-[#c9a961] mt-1">{testimonial.project}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-400">{testimonial.date}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-16 text-center">
          <p className="text-gray-600 mb-6">
            Rejoignez nos clients satisfaits et transformez votre intérieur
          </p>
          <button
            onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
            className="bg-[#c9a961] hover:bg-[#b89951] text-white font-semibold px-8 py-4 rounded-lg transition-all duration-300 hover:scale-105 inline-block"
          >
            Demander votre devis gratuit
          </button>
        </div>

        {/* Review Form */}
        <ReviewForm />

        {/* Company Info */}
        <div className="mt-12 text-center">
          <p className="text-sm text-gray-500">
            <strong>Domus & Co</strong> - 6 Rue d'Armaillé, 75017 Paris
          </p>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
